import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/config/api";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Edit, Trash2, X, Search, ExternalLink } from "lucide-react";
import { TablePagination } from "@/components/TablePagination";
import { showConfirm, showError } from "@/utils/dialog";
import { addDaysToLocalDateInputValue } from "@/utils/date";

const VENDOR_CATEGORIES = [
  { id: "hotel", label: "Hotel" },
  { id: "travel_agency", label: "Travel Agency" },
  { id: "insurance", label: "Insurance" },
  { id: "car_rental", label: "Car Rental" },
  { id: "visa_agent", label: "Visa Agent" },
  { id: "tour_operator", label: "Tour Operator" },
  { id: "other", label: "Other" },
];

export default function Vendors() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = String(user?.role || "").toLowerCase();
  const canManage = role === "admin" || role === "finance" || role === "management";
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const saved = localStorage.getItem("vendors.visibleColumns");
      return saved
        ? JSON.parse(saved)
        : {
            name: true,
            category: true,
            contact: true,
            emailPhone: true,
            payable: true,
            openingBalance: true,
            ledgerPayable: false,
            netBalance: true,
            address: false,
          };
    } catch {
      return {
        name: true,
        category: true,
        contact: true,
        emailPhone: true,
        payable: true,
        openingBalance: true,
        ledgerPayable: false,
        netBalance: true,
        address: false,
      };
    }
  });
  const [formData, setFormData] = useState({
    name: "",
    categories: ["hotel"],
    contact_name: "",
    email: "",
    phone: "",
    address: "",
    notes: "",
    opening_balance: "",
  });

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    try {
      const data = await api.vendors.getAll();
      setVendors(data);
    } catch (err) {
      console.error("Failed to fetch vendors:", err);
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditingVendor(null);
    setFormData({
      name: "",
      categories: ["hotel"],
      contact_name: "",
      email: "",
      phone: "",
      address: "",
      notes: "",
      opening_balance: "",
    });
    setError("");
    setShowForm(true);
  };

  const openEdit = (v) => {
    setEditingVendor(v);
    setFormData({
      name: v.name || "",
      categories:
        Array.isArray(v.categories) && v.categories.length
          ? v.categories
          : v.category
            ? [v.category]
            : ["hotel"],
      contact_name: v.contact_name || "",
      email: v.email || "",
      phone: v.phone || "",
      address: v.address || "",
      notes: v.notes || "",
      opening_balance: "",
    });
    setError("");
    setShowForm(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleCategory = (categoryId) => {
    setFormData((prev) => {
      const set = new Set(prev.categories || []);
      if (set.has(categoryId)) set.delete(categoryId);
      else set.add(categoryId);
      const next = [...set];
      return { ...prev, categories: next.length ? next : [categoryId] };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!formData.name.trim()) {
      setError("Vendor name is required");
      return;
    }
    if (!Array.isArray(formData.categories) || formData.categories.length === 0) {
      setError("Select at least one category");
      return;
    }
    setSubmitting(true);
    try {
      if (editingVendor) {
        const { opening_balance, ...updatePayload } = formData;
        await api.vendors.update(editingVendor.id, updatePayload);
      } else {
        await api.vendors.create(formData);
      }
      setShowForm(false);
      fetchVendors();
    } catch (err) {
      setError(err.message || "Failed to save vendor");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (vendor) => {
    const confirmed = await showConfirm(
      `Delete vendor "${vendor.name}"?`,
      "Delete Vendor",
      "Delete",
    );
    if (!confirmed) return;
    try {
      await api.vendors.delete(vendor.id);
      fetchVendors();
    } catch (err) {
      await showError(err.message || "Failed to delete vendor");
    }
  };

  const categoryLabel = (id) =>
    VENDOR_CATEGORIES.find((c) => c.id === id)?.label || id;
  const categoryItems = (vendor) => {
    const list = Array.isArray(vendor.categories) && vendor.categories.length
      ? vendor.categories
      : vendor.category
        ? [vendor.category]
        : [];
    return list.map((c) => ({ id: c, label: categoryLabel(c) }));
  };

  const filtered = useMemo(() => {
    let list = [...(vendors || [])];
    const s = (search || "").toLowerCase().trim();
    if (s) {
      list = list.filter(
        (v) =>
          (v.name || "").toLowerCase().includes(s) ||
          (v.contact_name || "").toLowerCase().includes(s) ||
          (v.email || "").toLowerCase().includes(s),
      );
    }
    if (categoryFilter) {
      list = list.filter((v) => {
        const cats = Array.isArray(v.categories) && v.categories.length
          ? v.categories
          : v.category
            ? [v.category]
            : [];
        return cats.includes(categoryFilter);
      });
    }
    return list;
  }, [vendors, search, categoryFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);
  const columnCount =
    (canManage ? 1 : 0) +
    (visibleColumns.name ? 1 : 0) +
    (visibleColumns.category ? 1 : 0) +
    (visibleColumns.contact ? 1 : 0) +
    (visibleColumns.emailPhone ? 1 : 0) +
    (visibleColumns.openingBalance ? 1 : 0) +
    (visibleColumns.payable ? 2 : 0) +
    (visibleColumns.ledgerPayable ? 1 : 0) +
    (visibleColumns.netBalance ? 1 : 0) +
    (visibleColumns.address ? 1 : 0);

  useEffect(() => {
    localStorage.setItem("vendors.visibleColumns", JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  useEffect(() => {
    setPage(1);
  }, [search, categoryFilter, pageSize]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-heading font-semibold text-secondary">
            Vendors
          </h1>
          <p className="text-xs text-stone-500 mt-1">
            {canManage
              ? "You can create and update vendors for operations."
              : "View-only mode. Raise a workflow ticket for any vendor update."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!canManage && (
            <button
              type="button"
              onClick={() =>
                navigate(
                  "/workflow-tickets?source_type=other&to_role=finance&title=Vendor%20update%20required&description=Please%20create%20or%20update%20vendor%20details.&due_date=" +
                    addDaysToLocalDateInputValue(1),
                )
              }
              className="border border-indigo-300 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 font-medium rounded-lg px-4 py-2 transition-all"
            >
              Generate Vendor Ticket
            </button>
          )}
          {canManage && (
            <button
              onClick={openAdd}
              className="bg-primary hover:bg-primary/90 text-white font-medium rounded-lg px-4 py-2 shadow-sm hover:shadow-md transition-all flex items-center gap-2"
            >
              <Plus size={20} />
              Add Vendor
            </button>
          )}
        </div>
      </div>

      <p className="text-stone-600 text-sm">
        Manage hotels, travel agencies, insurance, car rental, and other providers.
        Sales and operations teams use this master list in order/invoice workflows.
      </p>

      <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <input
              type="text"
              placeholder="Search by name, contact, or email..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full pl-9 pr-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
            />
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => {
              setCategoryFilter(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
          >
            <option value="">All categories</option>
            {VENDOR_CATEGORIES.map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-wrap gap-3 mt-3 text-xs text-stone-600">
          {[
            ["name", "Name"],
            ["category", "Category"],
            ["contact", "Contact"],
            ["emailPhone", "Email / Phone"],
            ["openingBalance", "Opening Balance"],
            ["payable", "Balances"],
            ["ledgerPayable", "Ledger Payable (Signed)"],
            ["netBalance", "Net Balance"],
          ].map(([key, label]) => (
            <label key={key} className="inline-flex items-center gap-1">
              <input
                type="checkbox"
                checked={visibleColumns[key]}
                onChange={(e) =>
                  setVisibleColumns((prev) => ({ ...prev, [key]: e.target.checked }))
                }
              />
              {label}
            </label>
          ))}
        </div>
        <p className="form-helper mt-2">Showing {filtered.length} of {vendors.length} vendor(s)</p>
      </div>

      <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                {visibleColumns.name && <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">Name</th>}
                {visibleColumns.category && <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">Category</th>}
                {visibleColumns.contact && <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">Contact</th>}
                {visibleColumns.emailPhone && <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">Email / Phone</th>}
                {visibleColumns.openingBalance && <th className="text-right py-3 px-4 text-sm font-medium text-stone-600">Opening Balance</th>}
                {visibleColumns.payable && <th className="text-right py-3 px-4 text-sm font-medium text-stone-600">Amount to Pay</th>}
                {visibleColumns.payable && <th className="text-right py-3 px-4 text-sm font-medium text-stone-600">Vendor Credit</th>}
                {visibleColumns.ledgerPayable && <th className="text-right py-3 px-4 text-sm font-medium text-stone-600">Ledger Payable (Signed)</th>}
                {visibleColumns.netBalance && <th className="text-right py-3 px-4 text-sm font-medium text-stone-600">Net Balance</th>}
                {visibleColumns.address && <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">Address</th>}
                {canManage && (
                  <th className="text-right py-3 px-4 text-sm font-medium text-stone-600">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={columnCount} className="py-8 text-center text-stone-500">
                    {vendors.length === 0 ? "No vendors yet. Add hotels, agencies, and other providers so sales can select them on orders." : "No vendors match your search or filter."}
                  </td>
                </tr>
              ) : (
                paginated.map((v) => (
                  <tr key={v.id} className="border-b border-stone-100 hover:bg-stone-50/50">
                    {visibleColumns.name && (
                      <td className="py-3 px-4 font-medium text-secondary">
                        <button
                          type="button"
                          onClick={() => navigate(`/vendors/${v.id}`)}
                          className="text-left hover:underline text-primary font-medium inline-flex items-center gap-1"
                        >
                          {v.name}
                          <ExternalLink size={14} className="opacity-50" />
                        </button>
                      </td>
                    )}
                    {visibleColumns.category && (
                      <td className="py-3 px-4 text-stone-600">
                        {categoryItems(v).length ? (
                          <div className="flex flex-wrap gap-1">
                            {categoryItems(v).map((c) => (
                              <span
                                key={`${v.id}-${c.id}`}
                                className="inline-flex items-center rounded-full border border-stone-200 bg-stone-50 px-2 py-0.5 text-xs text-stone-700"
                              >
                                {c.label}
                              </span>
                            ))}
                          </div>
                        ) : (
                          "—"
                        )}
                      </td>
                    )}
                    {visibleColumns.contact && <td className="py-3 px-4 text-stone-600">{v.contact_name || "—"}</td>}
                    {visibleColumns.emailPhone && <td className="py-3 px-4 text-stone-600 text-sm">
                      {v.email || "—"} {v.phone ? ` · ${v.phone}` : ""}
                    </td>}
                    {visibleColumns.openingBalance && (
                      <td className="py-3 px-4 text-right text-stone-700 font-mono text-sm">
                        {Number(v.opening_balance || 0).toFixed(2)}
                      </td>
                    )}
                    {visibleColumns.payable && (
                      <td className="py-3 px-4 text-right text-stone-800 font-mono text-sm">
                        {v.payable_balance != null
                          ? Number(v.payable_balance).toFixed(2)
                          : "—"}
                      </td>
                    )}
                    {visibleColumns.payable && (
                      <td className="py-3 px-4 text-right text-emerald-700 font-mono text-sm">
                        {v.vendor_credit_balance != null
                          ? Number(v.vendor_credit_balance).toFixed(2)
                          : "0.00"}
                      </td>
                    )}
                    {visibleColumns.ledgerPayable && (
                      <td className="py-3 px-4 text-right text-stone-800 font-mono text-sm">
                        {Number(v.ledger_payable || 0).toFixed(2)}
                      </td>
                    )}
                    {visibleColumns.netBalance && (
                      <td
                        className={`py-3 px-4 text-right font-mono text-sm ${
                          Number(v.payable_balance || 0) > 0 ? "text-amber-700" : "text-emerald-700"
                        }`}
                      >
                        {(Number(v.payable_balance || 0) - Number(v.vendor_credit_balance || 0)).toFixed(2)}
                      </td>
                    )}
                    {visibleColumns.address && (
                      <td className="py-3 px-4 text-stone-600 text-sm max-w-[200px] truncate" title={v.address || ""}>
                        {v.address || "—"}
                      </td>
                    )}
                    {canManage && (
                      <td className="py-3 px-4 text-right">
                        <button
                          onClick={() => openEdit(v)}
                          className="p-2 text-primary hover:bg-primary/10 rounded-lg"
                          title="Edit"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(v)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg ml-1"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <TablePagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          totalItems={filtered.length}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          sizeOptions={[10, 20, 30, 50, 100]}
        />
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-secondary">
                {editingVendor ? "Edit Vendor" : "Add Vendor"}
              </h2>
              <button
                onClick={() => setShowForm(false)}
                className="p-2 hover:bg-stone-100 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                {error}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Categories *</label>
                <div className="grid grid-cols-2 gap-2 border border-stone-300 rounded-lg p-3">
                  {VENDOR_CATEGORIES.map((c) => (
                    <label key={c.id} className="inline-flex items-center gap-2 text-sm text-stone-700">
                      <input
                        type="checkbox"
                        checked={(formData.categories || []).includes(c.id)}
                        onChange={() => toggleCategory(c.id)}
                      />
                      {c.label}
                    </label>
                  ))}
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {(formData.categories || []).map((categoryId) => (
                    <span
                      key={`selected-${categoryId}`}
                      className="inline-flex items-center rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-xs text-primary"
                    >
                      {categoryLabel(categoryId)}
                    </span>
                  ))}
                </div>
                <p className="form-helper mt-1">Select one or more categories for this vendor.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Contact name</label>
                <input
                  type="text"
                  name="contact_name"
                  value={formData.contact_name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Phone</label>
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Address</label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  rows={2}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary resize-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows={2}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary resize-none"
                />
              </div>
              {!editingVendor && (
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Opening balance</label>
                  <input
                    type="number"
                    step="0.01"
                    name="opening_balance"
                    value={formData.opening_balance}
                    onChange={handleChange}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                  <p className="form-helper mt-1">
                    Positive means payable to vendor; negative means vendor credit.
                  </p>
                </div>
              )}
              <div className="flex gap-2 justify-end pt-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 border border-stone-300 rounded-lg hover:bg-stone-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
                >
                  {submitting ? "Saving..." : editingVendor ? "Update" : "Add Vendor"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
